import React, { useRef, useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Tickmarks } from './Tickmarks';
import { Playhead } from './Playhead';
import { TimelineTrack } from './TimelineTrack';
import { SplitButton } from './SplitButton';
import { EditorDemoStore } from '../EditorDemoStore';
import { EditorStore } from '../EditorStore';
import { TimelineMode } from '../commands/TimelineMode';
import { Track } from '../models';

interface TimelineProps {
  demoStore: EditorDemoStore;
  editorStore: EditorStore;
  onElementSelect: (elementId: string, isMultiSelect?: boolean) => void;
}

export const Timeline = observer(
  ({ demoStore, editorStore, onElementSelect }: TimelineProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const tracksContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    // Calculate container width on mount and resize
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.clientWidth);
        }
      };

      updateWidth();

      const resizeObserver = new ResizeObserver(updateWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // Recalculate timeline scale when duration or container width changes
    useEffect(() => {
      // Set the tracks container reference for position calculation
      if (tracksContainerRef.current) {
        demoStore.setTracksContainerRef(tracksContainerRef);
      }
      
      if (containerWidth > 0) {
        demoStore.calculateDefaultScale(containerWidth);
      }
    }, [containerWidth, editorStore.timeline.duration, demoStore, tracksContainerRef]);

    // Unified click handler to set playhead position
    const handleTimelineClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        // Use the DemoStore's handleTimelineClick which uses the position manager
        demoStore.handleTimelineClick(e);
      },
      [demoStore],
    );

    // Handle keyboard navigation for frame-by-frame stepping
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        // Only process if the timeline component is focused or one of its children
        if (
          !containerRef.current?.contains(document.activeElement) &&
          document.activeElement !== document.body
        ) {
          return;
        }

        // Left/right arrow keys without modifiers to move by frames
        if (e.key === 'ArrowLeft' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          // Move backward by one frame
          const currentFrame = demoStore.getCurrentFrame();
          demoStore.setCurrentFrame(Math.max(0, currentFrame - 1));
        } else if (
          e.key === 'ArrowRight' &&
          !e.shiftKey &&
          !e.ctrlKey &&
          !e.altKey
        ) {
          e.preventDefault();
          // Move forward by one frame
          const currentFrame = demoStore.getCurrentFrame();
          const maxFrame = Math.ceil(
            (editorStore.timeline.duration || 0) * demoStore.getFramerate(),
          );
          demoStore.setCurrentFrame(Math.min(maxFrame, currentFrame + 1));
        }

        // Shift+Left/Right to move by 10 frames
        if (e.key === 'ArrowLeft' && e.shiftKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          const currentFrame = demoStore.getCurrentFrame();
          demoStore.setCurrentFrame(Math.max(0, currentFrame - 10));
        } else if (
          e.key === 'ArrowRight' &&
          e.shiftKey &&
          !e.ctrlKey &&
          !e.altKey
        ) {
          e.preventDefault();
          const currentFrame = demoStore.getCurrentFrame();
          const maxFrame = Math.ceil(
            (editorStore.timeline.duration || 0) * demoStore.getFramerate(),
          );
          demoStore.setCurrentFrame(Math.min(maxFrame, currentFrame + 10));
        }

        // Page Up/Down to move by larger increments (1 second)
        if (e.key === 'PageUp') {
          e.preventDefault();
          const oneSecondInFrames = demoStore.getFramerate();
          const currentFrame = demoStore.getCurrentFrame();
          demoStore.setCurrentFrame(
            Math.max(0, currentFrame - oneSecondInFrames),
          );
        } else if (e.key === 'PageDown') {
          e.preventDefault();
          const oneSecondInFrames = demoStore.getFramerate();
          const currentFrame = demoStore.getCurrentFrame();
          const maxFrame = Math.ceil(
            (editorStore.timeline.duration || 0) * demoStore.getFramerate(),
          );
          demoStore.setCurrentFrame(
            Math.min(maxFrame, currentFrame + oneSecondInFrames),
          );
        }

        // Home/End to move to start/end
        if (e.key === 'Home') {
          e.preventDefault();
          demoStore.setCurrentFrame(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          const maxFrame = Math.ceil(
            (editorStore.timeline.duration || 0) * demoStore.getFramerate(),
          );
          demoStore.setCurrentFrame(maxFrame);
        }

        // Space to toggle playback
        if (e.key === ' ' || e.code === 'Space') {
          e.preventDefault();
          demoStore.togglePlayback();
        }
      };

      // Add event listener to the document
      document.addEventListener('keydown', handleKeyDown);

      // Clean up the event listener on unmount
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [demoStore, editorStore.timeline.duration, containerRef]);

    // Calculate overall tracks height for the playhead
    const tracksHeight = editorStore.timeline.tracks.reduce(
      (height, track) => height + demoStore.getTrackHeight(track.id, track.type),
      0
    ) || 200;

    // Define a constant for the track label width
    const TRACK_LABEL_WIDTH = 120; // This should match the width in TrackHeader styled component

    return (
      <TimelineContainer
        ref={containerRef}
        tabIndex={0} // Make the container focusable
      >
        <TimelineControls>
          {/* Timeline mode selector */}
          <TimelineModeControls>
            <TimelineModeLabel>Mode:</TimelineModeLabel>
            <TimelineModeButton
              isActive={demoStore.timelineMode === TimelineMode.DEFAULT}
              onClick={() => demoStore.setTimelineMode(TimelineMode.DEFAULT)}
            >
              Default
            </TimelineModeButton>
            <TimelineModeButton
              isActive={demoStore.timelineMode === TimelineMode.RIPPLE}
              onClick={() => demoStore.setTimelineMode(TimelineMode.RIPPLE)}
            >
              Ripple
            </TimelineModeButton>
            <TimelineModeButton
              isActive={demoStore.timelineMode === TimelineMode.OVERLAP}
              onClick={() => demoStore.setTimelineMode(TimelineMode.OVERLAP)}
            >
              Overlap
            </TimelineModeButton>
          </TimelineModeControls>

          {/* Undo/redo controls */}
          <UndoRedoControls>
            <UndoRedoButton onClick={() => demoStore.undo()}>
              Undo
            </UndoRedoButton>
            <UndoRedoButton onClick={() => demoStore.redo()}>
              Redo
            </UndoRedoButton>
          </UndoRedoControls>

          {/* Frame controls */}
          <FrameControls>
            <FrameRateSelector
              value={demoStore.getFramerate().toString()}
              onChange={(e) => demoStore.setFramerate(Number(e.target.value))}
            >
              <option value="24">24 FPS</option>
              <option value="30">30 FPS</option>
              <option value="60">60 FPS</option>
            </FrameRateSelector>
            <SnapFrameToggle
              onClick={() =>
                demoStore.setSnapToFrame(!demoStore.getSnapToFrame())
              }
              isActive={demoStore.getSnapToFrame()}
            >
              {demoStore.getSnapToFrame() ? 'Snap On' : 'Snap Off'}
            </SnapFrameToggle>
            
            {/* Split button - only show when an element is selected and playhead is over it */}
            {editorStore.selectedElementIds.length === 1 && 
             (() => {
               const selectedElement = editorStore.selectedElements[0];
               if (selectedElement && 
                   demoStore.currentTime > selectedElement.time && 
                   demoStore.currentTime < selectedElement.time + selectedElement.durationSeconds) {
                 return (
                   <SplitButton 
                     onSplit={() => {
                       editorStore.splitElement(selectedElement.id, demoStore.currentTime);
                     }} 
                   />
                 );
               }
               return null;
             })()
            }
          </FrameControls>

          {/* Keyboard shortcuts info */}
          <KeyboardShortcuts>
            <KeyboardShortcutIcon>⌨️</KeyboardShortcutIcon>
            <KeyboardShortcutText>
              <ShortcutKey>←/→</ShortcutKey> Frame
              <ShortcutKey>Shift+←/→</ShortcutKey> 10 frames
              <ShortcutKey>PgUp/PgDn</ShortcutKey> 1 sec
              <ShortcutKey>Home/End</ShortcutKey> Start/End
              <ShortcutKey>Space</ShortcutKey> Play/pause
            </KeyboardShortcutText>
          </KeyboardShortcuts>

          {/* Zoom controls */}
          <ZoomControls>
            <ZoomButton onClick={() => demoStore.zoomOut()}>-</ZoomButton>
            <ZoomButton onClick={() => demoStore.zoomIn()}>+</ZoomButton>
          </ZoomControls>
        </TimelineControls>

        <TimelineLayout>
          {/* Top section with tickmarks */}
          <TickmarksSection>
            {/* Current frame display */}
            {demoStore.getSnapToFrame() && (
              <CurrentFrameDisplay>
                Frame: {demoStore.getCurrentFrame()} / Time:{' '}
                {demoStore.currentTime.toFixed(2)}s
              </CurrentFrameDisplay>
            )}

            {/* Label header placeholder - same width as labels column */}
            <TrackLabelHeader />

            {/* Tickmarks in the content area */}
            <TickmarksWrapper>
              <Tickmarks
                duration={editorStore.timeline.duration}
                timelineScale={demoStore.timelineScale}
                width={containerWidth - TRACK_LABEL_WIDTH}
                fps={demoStore.getFramerate()}
                showFrames={
                  demoStore.getSnapToFrame() && demoStore.timelineScale > 100
                }
                onClick={(time) => {
                  // Set playhead position when tickmarks are clicked
                  if (time !== null) {
                    demoStore.setTime(time, true);
                  }
                }}
              />
            </TickmarksWrapper>
          </TickmarksSection>

          {/* Unified scrollable tracks area */}
          <TracksScrollContainer>
            <TracksInnerContainer
              ref={tracksContainerRef}
              onClick={(e) => {
                // Focus the container for keyboard shortcuts 
                containerRef.current?.focus();
                // Don't process clicks here - let individual track content areas handle it
              }}
            >
              {editorStore.timeline.tracks.map((track) => {
                const trackHeight = demoStore.getTrackHeight(track.id, track.type);
                return (
                  <TrackRow key={track.id}>
                    {/* Track label */}
                    <TrackLabelColumn style={{ height: `${trackHeight}px` }}>
                      <TrackTypeLabel track={track} />
                    </TrackLabelColumn>

                    {/* Track content */}
                    <TrackContentColumn 
                      style={{ height: `${trackHeight}px` }}
                      onClick={handleTimelineClick} // Make entire track content area clickable for playhead
                    >
                      <TimelineTrack
                        track={track}
                        timelineScale={demoStore.timelineScale}
                        selectedElementIds={editorStore.selectedElementIds}
                        hideLabel={true}
                        onElementSelect={(elementId, e) => {
                          // Handle element selection
                          const isMultiSelect =
                            window.event &&
                            (window.event as KeyboardEvent).shiftKey;
                          onElementSelect(elementId, isMultiSelect);
                          
                          // Also update playhead position using the DemoStore's handler
                          if (e) {
                            demoStore.handleTimelineClick(e);
                          }
                        }}
                        onMoveElement={(elementId, newStartTime) =>
                          editorStore.moveElement(elementId, newStartTime)
                        }
                        onTrimElementStart={(elementId, newStartTime) =>
                          editorStore.trimElementStart(elementId, newStartTime)
                        }
                        onTrimElementEnd={(elementId, newEndTime) =>
                          editorStore.trimElementEnd(elementId, newEndTime)
                        }
                      />
                    </TrackContentColumn>
                  </TrackRow>
                );
              })}

              <Playhead
                currentTime={demoStore.currentTime}
                timelineScale={demoStore.timelineScale}
                trackHeight={tracksHeight}
                onDrag={(time) => demoStore.setTime(time, true)}
                onDragStart={() => demoStore.setPlayheadDragging(true)}
                onDragEnd={() => demoStore.setPlayheadDragging(false)}
                fps={demoStore.getFramerate()}
                showFrames={demoStore.getSnapToFrame()}
                offsetLeft={TRACK_LABEL_WIDTH} // Add offset to position playhead in content area
              />
            </TracksInnerContainer>
          </TracksScrollContainer>
        </TimelineLayout>
      </TimelineContainer>
    );
  },
);

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid #333;
  border-radius: 4px;
  overflow: hidden;
  background-color: #030419;

  /* Add styles for focus state to provide visual feedback */
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #0053d9;
  }

  /* Add a tooltip/instruction when focused */
  &:focus::after {
    content: 'Use arrow keys to navigate frames. Shift+arrows: 10 frames. Page Up/Down: 1 sec. Home/End: start/end. Space: play/pause.';
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #171724;
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0.9;
  }
`;

const TimelineControls = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: #171724;
  border-bottom: 1px solid #333;
  justify-content: space-between;
`;

const TimelineModeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimelineModeLabel = styled.span`
  font-size: 12px;
  color: #ccc;
`;

const TimelineModeButton = styled.button<{ isActive?: boolean }>`
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  background-color: ${(props) => (props.isActive ? '#0053D9' : '#333')};
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => (props.isActive ? '#0053D9' : '#444')};
  }
`;

const UndoRedoControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UndoRedoButton = styled.button`
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  background-color: #333;
  color: #fff;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background-color: #444;
  }
`;

const FrameControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FrameRateSelector = styled.select`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background-color: #333;
  color: #fff;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background-color: #444;
  }
`;

const SnapFrameToggle = styled.button<{ isActive?: boolean }>`
  padding: 4px 10px;
  border: none;
  border-radius: 4px;
  background-color: ${(props) => (props.isActive ? '#0053D9' : '#333')};
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => (props.isActive ? '#0053D9' : '#444')};
  }
`;

// Styled components for keyboard shortcuts display
const KeyboardShortcuts = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: 16px;
`;

const KeyboardShortcutIcon = styled.span`
  font-size: 16px;
`;

const KeyboardShortcutText = styled.div`
  font-size: 11px;
  color: #aaa;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ShortcutKey = styled.kbd`
  display: inline-block;
  padding: 2px 4px;
  font-size: 10px;
  font-family: monospace;
  line-height: 10px;
  color: #fff;
  background-color: #333;
  border: 1px solid #555;
  border-radius: 3px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
  margin: 0 2px;
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
`;

const ZoomButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin: 0 4px;
  border: none;
  border-radius: 4px;
  background-color: #333;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #444;
  }
`;

// New layout components for the unified scrolling
const TimelineLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TickmarksSection = styled.div`
  display: flex;
  width: 100%;
  height: 24px;
  border-bottom: 1px solid #333;
`;

const TickmarksWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;

const TrackLabelHeader = styled.div`
  width: 120px;
  min-width: 120px;
  height: 100%;
  background-color: #171724;
  border-right: 1px solid #333;
`;

const TracksScrollContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: auto;
  /* This is the key for synchronized scrolling - a single scroll container */
`;

const TracksInnerContainer = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
`;

const TrackRow = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 1px solid #333;
  position: relative;
`;

const TrackLabelColumn = styled.div`
  width: 120px;
  min-width: 120px;
  background-color: #171724;
  border-right: 1px solid #333;
  display: flex;
  align-items: center;
`;

const TrackContentColumn = styled.div`
  flex: 1;
  position: relative;
`;

// Component to display track type label
const TrackTypeLabel: React.FC<{ track: Track }> = ({ track }) => {
  // Get track icon and label
  const getTrackInfo = () => {
    switch (track.type) {
      case 'video':
        return { label: 'Video', icon: '🎬' };
      case 'text':
        return { label: 'Text', icon: 'T' };
      case 'audio':
        return { label: 'Audio', icon: '🔊' };
      case 'image':
        return { label: 'Image', icon: '🖼️' };
      default:
        return { label: track.type, icon: '◆' };
    }
  };

  const trackInfo = getTrackInfo();

  return (
    <TrackLabelContent>
      <TrackIcon>{trackInfo.icon}</TrackIcon>
      <TrackLabel>{trackInfo.label}</TrackLabel>
    </TrackLabelContent>
  );
};

const TrackLabelContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 8px;
`;

const TrackIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  border-radius: 12px;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.1);
`;

const TrackLabel = styled.div`
  color: #f3e9d7;
  font-size: 12px;
  font-weight: 500;
`;

const CurrentFrameDisplay = styled.div`
  position: absolute;
  top: 0;
  right: 10px;
  background-color: #171724;
  color: #fff;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 0 0 4px 4px;
  z-index: 10;
  font-family: monospace;
`;

// No longer needed - removed
